import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { Plus, Edit2, Trash2, Download, FileText, Clock, CheckCircle, XCircle, ArrowRight, X, FileCheck, Eye, RotateCcw } from "lucide-react";
import api, { BACKEND_URL } from "@/lib/api";
import PDFPreviewModal from "@/components/PDFPreviewModal";

const STATUS_CONFIG = {
  draft: { label: "Draft", color: "bg-gray-500/20 text-gray-400", icon: FileText },
  sent: { label: "Sent", color: "bg-blue-500/20 text-blue-400", icon: Clock },
  accepted: { label: "Accepted", color: "bg-green-500/20 text-green-400", icon: CheckCircle },
  declined: { label: "Declined", color: "bg-red-500/20 text-red-400", icon: XCircle },
  expired: { label: "Expired", color: "bg-orange-500/20 text-orange-400", icon: Clock },
  converted: { label: "Converted", color: "bg-purple-500/20 text-purple-400", icon: FileCheck },
};

export default function QuotesPage({ companyId, company }) {
  const [quotes, setQuotes] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("");
  
  // Dialog state
  const [showDialog, setShowDialog] = useState(false);
  const [editingQuote, setEditingQuote] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Preview modal state
  const [previewQuote, setPreviewQuote] = useState(null);
  
  // Form state
  const [customerId, setCustomerId] = useState("");
  const [issueDate, setIssueDate] = useState(new Date().toISOString().split("T")[0]);
  const [expirationDate, setExpirationDate] = useState("");
  const [taxRate, setTaxRate] = useState("");
  const [scopeOfServices, setScopeOfServices] = useState("");
  const [notes, setNotes] = useState("");
  const [termsAndConditions, setTermsAndConditions] = useState("");
  const [lineItems, setLineItems] = useState([{ description: "", quantity: 1, unit_price: 0 }]);

  const loadData = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const params = filterStatus ? `?status=${filterStatus}` : "";
      const [quotesRes, customersRes] = await Promise.all([
        api.get(`/companies/${companyId}/quotes${params}`),
        api.get(`/companies/${companyId}/customers`)
      ]);
      
      setQuotes(quotesRes.data.quotes || []);
      setStats(quotesRes.data.stats || {});
      setCustomers(customersRes.data.customers || []);
    } catch (err) {
      toast.error("Failed to load quotes");
    } finally {
      setLoading(false);
    }
  }, [companyId, filterStatus]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (company) {
      setTaxRate(String(company.default_tax_rate || 0));
    }
  }, [company]);

  const getDefaultExpiration = () => {
    const date = new Date();
    date.setDate(date.getDate() + 30);
    return date.toISOString().split("T")[0];
  };

  const resetForm = () => {
    setCustomerId("");
    setIssueDate(new Date().toISOString().split("T")[0]);
    setExpirationDate(getDefaultExpiration());
    setTaxRate(String(company?.default_tax_rate || 0));
    setScopeOfServices("");
    setNotes("");
    setTermsAndConditions("");
    setLineItems([{ description: "", quantity: 1, unit_price: 0 }]);
    setEditingQuote(null);
  };

  const openNewDialog = () => {
    resetForm();
    setShowDialog(true);
  };

  const openEditDialog = async (quote) => {
    try {
      const res = await api.get(`/companies/${companyId}/quotes/${quote.quote_id}`);
      const q = res.data;
      setEditingQuote(q);
      setCustomerId(q.customer_id);
      setIssueDate(q.issue_date);
      setExpirationDate(q.expiration_date);
      setTaxRate(String(q.tax_rate));
      setScopeOfServices(q.scope_of_services || "");
      setNotes(q.notes || "");
      setTermsAndConditions(q.terms_and_conditions || "");
      setLineItems(q.line_items?.length ? q.line_items : [{ description: "", quantity: 1, unit_price: 0 }]);
      setShowDialog(true);
    } catch (err) {
      toast.error("Failed to load quote details");
    }
  };

  const handleSave = async () => {
    if (!customerId) {
      toast.error("Please select a customer");
      return;
    }
    if (!lineItems.some(item => item.description.trim())) {
      toast.error("Please add at least one line item");
      return;
    }
    
    setSaving(true);
    try {
      const data = {
        customer_id: customerId,
        issue_date: issueDate,
        expiration_date: expirationDate,
        tax_rate: parseFloat(taxRate) || 0,
        scope_of_services: scopeOfServices.trim(),
        notes: notes.trim(),
        terms_and_conditions: termsAndConditions.trim(),
        line_items: lineItems.filter(item => item.description.trim()).map(item => ({
          description: item.description.trim(),
          quantity: parseFloat(item.quantity) || 1,
          unit_price: parseFloat(item.unit_price) || 0
        }))
      };
      
      if (editingQuote) {
        await api.put(`/companies/${companyId}/quotes/${editingQuote.quote_id}`, data);
        toast.success("Quote updated");
      } else {
        await api.post(`/companies/${companyId}/quotes`, data);
        toast.success("Quote created");
      }
      
      setShowDialog(false);
      resetForm();
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save quote");
    } finally {
      setSaving(false);
    }
  };

  const handleConvert = async (quote) => {
    if (!confirm(`Convert quote ${quote.quote_number} to an invoice?`)) return;
    
    try {
      const res = await api.post(`/companies/${companyId}/quotes/${quote.quote_id}/convert`);
      toast.success(`Quote converted to invoice ${res.data.invoice.invoice_number}`);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to convert quote");
    }
  };

  const handleRevertConversion = async (quote) => {
    if (!confirm(`Revert quote ${quote.quote_number} back to "Accepted" status?\n\nThis will allow you to edit the quote and convert it to a new invoice.\n\nNote: Any existing invoice created from this quote will NOT be deleted.`)) return;
    
    try {
      await api.post(`/companies/${companyId}/quotes/${quote.quote_id}/revert`);
      toast.success(`Quote ${quote.quote_number} reverted to "Accepted" status. You can now edit it.`);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to revert quote");
    }
  };

  const handleDelete = async (quote) => {
    if (!["draft", "declined", "expired"].includes(quote.status)) {
      toast.error("Only draft, declined, or expired quotes can be deleted");
      return;
    }
    if (!confirm(`Delete quote ${quote.quote_number}?`)) return;
    
    try {
      await api.delete(`/companies/${companyId}/quotes/${quote.quote_id}`);
      toast.success("Quote deleted");
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete quote");
    }
  };

  const handleDownloadPDF = async (quote) => {
    // Open preview modal instead of direct download
    setPreviewQuote(quote);
  };

  const handleUpdateStatus = async (quote, newStatus) => {
    try {
      await api.put(`/companies/${companyId}/quotes/${quote.quote_id}`, { status: newStatus });
      toast.success(`Quote marked as ${newStatus}`);
      loadData();
    } catch (err) {
      toast.error("Failed to update status");
    }
  };

  const addLineItem = () => {
    setLineItems([...lineItems, { description: "", quantity: 1, unit_price: 0 }]);
  };

  const removeLineItem = (index) => {
    if (lineItems.length > 1) {
      setLineItems(lineItems.filter((_, i) => i !== index));
    }
  };

  const updateLineItem = (index, field, value) => {
    const updated = [...lineItems];
    updated[index] = { ...updated[index], [field]: value };
    setLineItems(updated);
  };

  const calculateTotals = () => {
    const subtotal = lineItems.reduce((sum, item) => {
      return sum + (parseFloat(item.quantity) || 0) * (parseFloat(item.unit_price) || 0);
    }, 0);
    const tax = subtotal * (parseFloat(taxRate) || 0) / 100;
    return { subtotal, tax, total: subtotal + tax };
  };

  const totals = calculateTotals();

  if (!companyId) {
    return (
      <div className="flex items-center justify-center h-64 text-muted-foreground">
        Select a company to view quotes
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="quotes-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-heading font-bold text-[#d4c4b0]">Quotes & Estimates</h1>
          <p className="text-[#8b9299] text-sm mt-1">Create quotes and convert them to invoices</p>
        </div>
        <Button onClick={openNewDialog} className="btn-primary-gradient" disabled={customers.length === 0} data-testid="create-quote-btn">
          <Plus className="w-4 h-4 mr-2" /> New Quote
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-card border-border card-glow">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                <FileText className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <p className="text-xs text-[#8b9299]">Total Quotes</p>
                <p className="text-xl font-bold text-[#d4c4b0]">{stats.total_count || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border card-glow">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-green-400" />
              </div>
              <div>
                <p className="text-xs text-[#8b9299]">Accepted</p>
                <p className="text-xl font-bold text-[#d4c4b0]">{stats.accepted || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border card-glow">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                <FileCheck className="w-5 h-5 text-purple-400" />
              </div>
              <div>
                <p className="text-xs text-[#8b9299]">Converted</p>
                <p className="text-xl font-bold text-[#d4c4b0]">{stats.converted || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border card-glow">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-orange-500/20 flex items-center justify-center">
                <Clock className="w-5 h-5 text-orange-400" />
              </div>
              <div>
                <p className="text-xs text-[#8b9299]">Pending</p>
                <p className="text-xl font-bold text-[#d4c4b0]">{(stats.draft || 0) + (stats.sent || 0)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2">
        {["", "draft", "sent", "accepted", "converted", "declined", "expired"].map(status => (
          <Button
            key={status}
            variant={filterStatus === status ? "default" : "outline"}
            size="sm"
            onClick={() => setFilterStatus(status)}
            className={filterStatus === status ? "btn-primary-gradient" : "border-border text-[#8b9299]"}
          >
            {status ? STATUS_CONFIG[status]?.label : "All"} {status && `(${stats[status] || 0})`}
          </Button>
        ))}
      </div>

      {/* Quote List */}
      <Card className="bg-card border-border">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-48">
              <div className="w-6 h-6 border-2 border-[#e85d24] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : customers.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-48 text-[#6b7280]">
              <FileText className="w-12 h-12 mb-3 opacity-50" />
              <p>Add customers first to create quotes</p>
              <Button variant="link" onClick={() => window.location.href = "/customers"} className="text-[#e85d24] mt-2">
                Go to Customers
              </Button>
            </div>
          ) : quotes.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-48 text-[#6b7280]">
              <FileText className="w-12 h-12 mb-3 opacity-50" />
              <p>No quotes found</p>
              <Button variant="link" onClick={openNewDialog} className="text-[#e85d24] mt-2">
                Create your first quote
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-border hover:bg-transparent">
                    <TableHead className="text-[#8b9299]">Quote #</TableHead>
                    <TableHead className="text-[#8b9299]">Customer</TableHead>
                    <TableHead className="text-[#8b9299]">Date</TableHead>
                    <TableHead className="text-[#8b9299]">Expires</TableHead>
                    <TableHead className="text-[#8b9299]">Status</TableHead>
                    <TableHead className="text-[#8b9299] text-right">Amount</TableHead>
                    <TableHead className="text-[#8b9299] text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {quotes.map((quote) => {
                    const config = STATUS_CONFIG[quote.status] || STATUS_CONFIG.draft;
                    const isExpired = new Date(quote.expiration_date) < new Date() && !["converted", "accepted", "declined", "expired"].includes(quote.status);
                    return (
                      <TableRow key={quote.quote_id} className="border-border hover:bg-secondary/50" data-testid={`quote-row-${quote.quote_id}`}>
                        <TableCell className="text-[#d4c4b0] font-medium font-mono">{quote.quote_number}</TableCell>
                        <TableCell className="text-[#d4c4b0]">{quote.customer_name}</TableCell>
                        <TableCell className="text-[#8b9299]">{quote.issue_date}</TableCell>
                        <TableCell className={isExpired ? "text-red-400" : "text-[#8b9299]"}>{quote.expiration_date}</TableCell>
                        <TableCell>
                          <Badge className={config.color}>{config.label}</Badge>
                        </TableCell>
                        <TableCell className="text-[#d4c4b0] text-right font-mono">${quote.total?.toLocaleString()}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button variant="ghost" size="icon" className="h-8 w-8 text-primary hover:text-primary/80" onClick={() => setPreviewQuote(quote)} title="Preview PDF" data-testid={`preview-quote-${quote.quote_id}`}>
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="icon" className="h-8 w-8 text-[#8b9299] hover:text-[#d4c4b0]" onClick={() => handleDownloadPDF(quote)} title="Download PDF">
                              <Download className="w-4 h-4" />
                            </Button>
                            {quote.status === "draft" && (
                              <>
                                <Button variant="ghost" size="icon" className="h-8 w-8 text-[#8b9299] hover:text-[#d4c4b0]" onClick={() => openEditDialog(quote)} title="Edit">
                                  <Edit2 className="w-4 h-4" />
                                </Button>
                                <Button variant="ghost" size="icon" className="h-8 w-8 text-blue-400 hover:text-blue-300" onClick={() => handleUpdateStatus(quote, "sent")} title="Mark as Sent">
                                  <Clock className="w-4 h-4" />
                                </Button>
                              </>
                            )}
                            {quote.status === "sent" && (
                              <>
                                <Button variant="ghost" size="icon" className="h-8 w-8 text-green-400 hover:text-green-300" onClick={() => handleUpdateStatus(quote, "accepted")} title="Mark as Accepted">
                                  <CheckCircle className="w-4 h-4" />
                                </Button>
                                <Button variant="ghost" size="icon" className="h-8 w-8 text-red-400 hover:text-red-300" onClick={() => handleUpdateStatus(quote, "declined")} title="Mark as Declined">
                                  <XCircle className="w-4 h-4" />
                                </Button>
                              </>
                            )}
                            {(quote.status === "accepted" || quote.status === "sent") && quote.status !== "converted" && (
                              <Button variant="ghost" size="icon" className="h-8 w-8 text-purple-400 hover:text-purple-300" onClick={() => handleConvert(quote)} title="Convert to Invoice">
                                <ArrowRight className="w-4 h-4" />
                              </Button>
                            )}
                            {quote.status === "converted" && (
                              <Button variant="ghost" size="icon" className="h-8 w-8 text-amber-400 hover:text-amber-300" onClick={() => handleRevertConversion(quote)} title="Revert to Accepted (undo conversion)" data-testid={`revert-quote-${quote.quote_id}`}>
                                <RotateCcw className="w-4 h-4" />
                              </Button>
                            )}
                            {["draft", "declined", "expired"].includes(quote.status) && (
                              <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive" onClick={() => handleDelete(quote)} title="Delete">
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="bg-card border-border max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="quote-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-white">
              {editingQuote ? `Edit Quote ${editingQuote.quote_number}` : "Create Quote"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {/* Customer & Dates */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label className="text-[#8b9299] text-sm">Customer *</Label>
                <Select value={customerId} onValueChange={setCustomerId}>
                  <SelectTrigger className="mt-1.5 bg-secondary border-border text-white" data-testid="quote-customer-select">
                    <SelectValue placeholder="Select customer" />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-border max-h-48">
                    {customers.map(c => (
                      <SelectItem key={c.customer_id} value={c.customer_id} className="text-white">{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[#8b9299] text-sm">Issue Date</Label>
                <Input type="date" value={issueDate} onChange={(e) => setIssueDate(e.target.value)} className="mt-1.5 bg-secondary border-border text-white" data-testid="quote-issue-date" />
              </div>
              <div>
                <Label className="text-[#8b9299] text-sm">Valid Until</Label>
                <Input type="date" value={expirationDate} onChange={(e) => setExpirationDate(e.target.value)} className="mt-1.5 bg-secondary border-border text-white" data-testid="quote-expiration-date" />
              </div>
            </div>

            <div>
              <Label className="text-[#8b9299] text-sm">Tax Rate (%)</Label>
              <Input type="number" step="0.01" min="0" value={taxRate} onChange={(e) => setTaxRate(e.target.value)} className="mt-1.5 bg-secondary border-border text-white w-32" data-testid="quote-tax-rate" />
            </div>

            <Separator className="bg-border/50" />

            {/* Scope of Services */}
            <div>
              <Label className="text-[#d4c4b0] font-medium">Scope of Services</Label>
              <p className="text-[#6b7280] text-xs mt-0.5 mb-2">Provide a general description of the project or services being quoted</p>
              <Textarea 
                value={scopeOfServices} 
                onChange={(e) => setScopeOfServices(e.target.value)} 
                placeholder="Describe the overall scope of work, project objectives, deliverables, and any key assumptions or requirements..."
                className="bg-secondary border-border text-white"
                rows={4}
                data-testid="quote-scope-of-services"
              />
            </div>

            <Separator className="bg-border/50" />
            
            {/* Line Items */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <div>
                  <Label className="text-[#d4c4b0] font-medium">Line Items</Label>
                  <p className="text-[#6b7280] text-xs">Detail each service or product with pricing</p>
                </div>
                <Button type="button" variant="outline" size="sm" onClick={addLineItem} className="border-border text-[#8b9299]">
                  <Plus className="w-3 h-3 mr-1" /> Add Item
                </Button>
              </div>
              <div className="space-y-3">
                {lineItems.map((item, index) => (
                  <div key={index} className="p-3 bg-secondary/30 rounded-lg border border-border/50">
                    <div className="flex justify-between items-start mb-2">
                      <Label className="text-[#8b9299] text-xs">Item {index + 1}</Label>
                      {lineItems.length > 1 && (
                        <Button type="button" variant="ghost" size="icon" className="h-6 w-6 text-destructive" onClick={() => removeLineItem(index)}>
                          <X className="w-3 h-3" />
                        </Button>
                      )}
                    </div>
                    <div className="space-y-2">
                      <div>
                        <Label className="text-[#6b7280] text-xs">Description</Label>
                        <Textarea 
                          value={item.description} 
                          onChange={(e) => updateLineItem(index, "description", e.target.value)} 
                          placeholder="Describe the service or product in detail..."
                          className="bg-secondary border-border text-white mt-1"
                          rows={2}
                          data-testid={`quote-line-item-desc-${index}`}
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-[#6b7280] text-xs">Quantity</Label>
                          <Input type="number" step="0.01" min="0" value={item.quantity} onChange={(e) => updateLineItem(index, "quantity", e.target.value)} className="bg-secondary border-border text-white mt-1" />
                        </div>
                        <div>
                          <Label className="text-[#6b7280] text-xs">Unit Price ($)</Label>
                          <Input type="number" step="0.01" min="0" value={item.unit_price} onChange={(e) => updateLineItem(index, "unit_price", e.target.value)} placeholder="0.00" className="bg-secondary border-border text-white mt-1" />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Totals */}
            <div className="flex justify-end">
              <div className="w-64 space-y-1 text-sm">
                <div className="flex justify-between text-[#8b9299]">
                  <span>Subtotal:</span>
                  <span>${totals.subtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-[#8b9299]">
                  <span>Tax ({taxRate || 0}%):</span>
                  <span>${totals.tax.toFixed(2)}</span>
                </div>
                <Separator className="bg-border/50 my-1" />
                <div className="flex justify-between text-[#d4c4b0] font-bold">
                  <span>Total:</span>
                  <span>${totals.total.toFixed(2)}</span>
                </div>
              </div>
            </div>

            <Separator className="bg-border/50" />

            {/* Notes & Terms */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-[#8b9299] text-sm">Notes</Label>
                <p className="text-[#6b7280] text-xs mt-0.5 mb-1">Use - for bullet points, **text** for bold</p>
                <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Additional notes for the customer...&#10;- First point&#10;- Second point" className="bg-secondary border-border text-white" rows={4} data-testid="quote-notes" />
              </div>
              <div>
                <Label className="text-[#8b9299] text-sm">Terms & Conditions</Label>
                <p className="text-[#6b7280] text-xs mt-0.5 mb-1">Use - for bullet points, **text** for bold</p>
                <Textarea value={termsAndConditions} onChange={(e) => setTermsAndConditions(e.target.value)} placeholder="Payment terms, warranty, etc.&#10;- 50% deposit required&#10;- Net 30 payment terms" className="bg-secondary border-border text-white" rows={4} data-testid="quote-terms" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)} className="border-border text-white">Cancel</Button>
            <Button onClick={handleSave} disabled={saving || !customerId} className="btn-primary-gradient" data-testid="save-quote-btn">
              {saving ? "Saving..." : (editingQuote ? "Update Quote" : "Create Quote")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* PDF Preview Modal */}
      <PDFPreviewModal
        open={!!previewQuote}
        onClose={() => setPreviewQuote(null)}
        title="Quote Preview"
        documentType="quote"
        companyId={companyId}
        documentId={previewQuote?.quote_id}
        documentNumber={previewQuote?.quote_number}
      />
    </div>
  );
}
